"use client";

import { ArrowDown } from "lucide-react";
import type { ReactNode } from "react";
import { createContext, useContext, useId, useRef, useState } from "react";
import { StudioActionMenu } from "@/studio/components/chrome/StudioActionMenu";

/**
 * Extra class for the picker of every CursorSelect below this provider —
 * phone sheets use it to open taller, touch-sized pickers.
 */
export const CursorSelectMenuClassContext = createContext("");

export type CursorSelectTone = "good" | "warn" | "bad" | "info" | "muted";

export type CursorSelectOption = {
  value: string;
  label: string;
  /** Prefer a Lucide (or Icon) node in front of the label. */
  icon?: ReactNode;
  /** Quiet meta on the right (currency, count). Never bake it into `label`. */
  detail?: string;
  /** Color-codes the leading status icon (good/warn/bad/info/muted). */
  tone?: CursorSelectTone;
  /** Shown greyed out and cannot be picked. */
  disabled?: boolean;
};

type CursorSelectProps = {
  value?: string;
  values?: string[];
  options?: CursorSelectOption[];
  onChange?: (value: string) => void;
  onValuesChange?: (values: string[]) => void;
  ariaLabel?: string;
  align?: "start" | "end";
  /** Bordered full-width field (sidebars/forms). Default is a ghost chrome control. */
  variant?: "ghost" | "field";
  className?: string;
  disabled?: boolean;
  multiple?: boolean;
  placeholder?: string;
  /** Trigger-only leading icon (does not repeat on every menu row). */
  leading?: ReactNode;
  describedBy?: string;
};

function toneClass(tone?: CursorSelectTone): string {
  if (!tone || tone === "muted") return " is-muted";
  return ` is-${tone}`;
}

/**
 * Compact themed select — same menu language as explorer filters
 * (.cursor-dropdown / .cursor-dropdown-item).
 * Root class is cursor-select-menu (not cursor-select) so native
 * select.cursor-select styles never paint a second box around it.
 * Prefer options with a leading icon; use tone for status color.
 * The menu portals to document.body and anchors to the trigger so
 * overflow/transform parents (DM bubbles, swipe rows) cannot detach it.
 * Placement (flip/shift, end-align near the right edge) is placeFloating's.
 */
export function CursorSelect({
  value = "",
  values,
  options = [],
  onChange,
  onValuesChange,
  ariaLabel,
  align = "end",
  variant = "ghost",
  className = "",
  disabled = false,
  multiple = false,
  placeholder,
  leading,
  describedBy,
}: CursorSelectProps) {
  const [open, setOpen] = useState(false);
  const menuClassName = useContext(CursorSelectMenuClassContext);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listId = useId();
  const selectedValues = multiple ? values ?? [] : value ? [value] : [];
  const active = multiple
    ? null
    : options.find((opt) => opt.value === value) ?? (placeholder ? null : options[0] ?? null);
  const triggerLabel = multiple
    ? options
        .filter((opt) => selectedValues.includes(opt.value))
        .map((opt) => opt.label)
        .join(", ") ||
      placeholder ||
      "Select"
    : active?.label ?? placeholder ?? "Select";
  const showingPlaceholder = multiple
    ? selectedValues.length === 0
    : !active;

  return (
    <div
      className={`cursor-select-menu${variant === "field" ? " is-field" : ""}${open ? " is-open" : ""}${className ? ` ${className}` : ""}`}
      ref={wrapRef}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`cursor-select-trigger${showingPlaceholder ? " is-placeholder" : ""}`}
        disabled={disabled}
        title={open ? undefined : ariaLabel}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        {leading || active?.icon ? (
          <span
            className={`cursor-select-leading${toneClass(active?.tone)}`}
            aria-hidden="true"
          >
            {leading ?? active?.icon}
          </span>
        ) : null}
        <span className="cursor-select-label">{triggerLabel}</span>
        {active?.detail ? (
          <span className="cursor-select-detail">{active.detail}</span>
        ) : null}
        <ArrowDown className="cursor-select-arrow" aria-hidden="true" />
      </button>
      {open ? (
        <StudioActionMenu
          label={ariaLabel || "Choose"}
          menuClassName={menuClassName || undefined}
          point={{
            x: triggerRef.current?.getBoundingClientRect().left ?? 8,
            y: (triggerRef.current?.getBoundingClientRect().bottom ?? 8) + 6,
          }}
          anchorRect={triggerRef.current?.getBoundingClientRect() ?? null}
          anchorAlign={align}
          placement="below"
          menuWidth={variant === "field" ? triggerRef.current?.getBoundingClientRect().width : undefined}
          menuMinWidth={triggerRef.current?.getBoundingClientRect().width}
          onClose={() => setOpen(false)}
          items={options.map((opt) => {
            const selected = selectedValues.includes(opt.value);
            return {
              id: opt.value,
              label: opt.detail ? `${opt.label}  ${opt.detail}` : opt.label,
              icon: opt.icon,
              selected,
              disabled: opt.disabled,
              section: "actions",
              closeOnSelect: !multiple,
              onSelect: () => {
                if (multiple) {
                  const next = selected
                    ? selectedValues.filter((item) => item !== opt.value)
                    : [...selectedValues, opt.value];
                  onValuesChange?.(next);
                  return;
                }
                onChange?.(opt.value);
              },
            };
          })}
        />
      ) : null}
    </div>
  );
}
